import type { TierConfig, TierLevel, FeatureFlags, TierLayoutConfig } from './api'

// ─────────────────────────────────────────────────────────────────────────────
// Default Feature Flags per Tier
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_FEATURES: Record<TierLevel, FeatureFlags> = {
  free: {
    hidePoweredBy: false,
    maxTripsDisplayed: 10,
    maxPinnedDisplayed: 4,
    showGallery: false,
    // Highlight sections - minimal for free tier
    showWhyBook: false,
    showQuickFacts: true,
    showSupport: false,
    showPickupPoints: false,
    showDestinations: false,
    showSafety: false,
    showCancellation: false,
    showUpcoming: true,
    showPayments: false,
    showUpiPay: false,
    showLanguages: false,
    showAwards: false,
    showDownloads: false,
    showTrust: false,
    showFaq: true,
  },
  basic: {
    // Paid plan on the free layout — identical to free except the display caps
    // are lifted (the Basic plan sells unlimited trips).
    hidePoweredBy: false,
    maxTripsDisplayed: -1,
    maxPinnedDisplayed: -1,
    showGallery: false,
    showWhyBook: false,
    showQuickFacts: true,
    showSupport: false,
    showPickupPoints: false,
    showDestinations: false,
    showSafety: false,
    showCancellation: false,
    showUpcoming: true,
    showPayments: false,
    showUpiPay: false,
    showLanguages: false,
    showAwards: false,
    showDownloads: false,
    showTrust: false,
    showFaq: true,
  },
  premium: {
    hidePoweredBy: true,
    maxTripsDisplayed: -1,
    maxPinnedDisplayed: -1,
    showGallery: true,
    // Highlight sections - all enabled for premium
    showWhyBook: true,
    showQuickFacts: true,
    showSupport: true,
    showPickupPoints: true,
    showDestinations: true,
    showSafety: true,
    showCancellation: true,
    showUpcoming: true,
    showPayments: true,
    showUpiPay: false,
    showLanguages: true,
    showAwards: true,
    showDownloads: true,
    showTrust: true,
    showFaq: true,
  },
  enterprise: {
    hidePoweredBy: true,
    maxTripsDisplayed: -1,
    maxPinnedDisplayed: -1,
    showGallery: true,
    // Highlight sections - all enabled for enterprise
    showWhyBook: true,
    showQuickFacts: true,
    showSupport: true,
    showPickupPoints: true,
    showDestinations: true,
    showSafety: true,
    showCancellation: true,
    showUpcoming: true,
    showPayments: true,
    showUpiPay: false,
    showLanguages: true,
    showAwards: true,
    showDownloads: true,
    showTrust: true,
    showFaq: true,
  },
}

// ─────────────────────────────────────────────────────────────────────────────
// Default Layout Configurations per Tier
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_LAYOUTS: Record<TierLevel, TierLayoutConfig> = {
  free: {
    heroVariant: 'compact',
    headerVariant: 'pill',
    highlightCardVariant: 'variant-1',
    tripCardVariant: 'variant-1',
    galleryStyle: 'grid',
    footerStyle: 'minimal',
  },
  basic: {
    // Same layout as free — basic pays for the trip cap lift, not the premium layout
    heroVariant: 'compact',
    headerVariant: 'pill',
    highlightCardVariant: 'variant-1',
    tripCardVariant: 'variant-1',
    galleryStyle: 'grid',
    footerStyle: 'minimal',
  },
  premium: {
    heroVariant: 'standard',
    headerVariant: 'pill',
    highlightCardVariant: 'variant-1',
    tripCardVariant: 'variant-1',
    galleryStyle: 'carousel',
    footerStyle: 'standard',
  },
  enterprise: {
    heroVariant: 'immersive',
    headerVariant: 'pill',
    highlightCardVariant: 'variant-1',
    tripCardVariant: 'variant-1',
    galleryStyle: 'masonry',
    footerStyle: 'premium',
  },
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get the complete default tier configuration for a given tier level.
 * Defaults to 'free' tier if not specified.
 */
export function getDefaultTierConfig(tier: TierLevel = 'free'): TierConfig {
  return {
    tier,
    features: { ...DEFAULT_FEATURES[tier] },
    layout: { ...DEFAULT_LAYOUTS[tier] },
  }
}

/**
 * Merge a partial tier configuration with defaults.
 * This ensures backward compatibility - vendors without tierConfig get free tier.
 * Partial feature/layout overrides are merged with tier defaults.
 */
export function mergeTierConfig(partial?: Partial<TierConfig>): TierConfig {
  const tier = partial?.tier || 'free'
  const defaults = getDefaultTierConfig(tier)

  return {
    tier,
    features: { ...defaults.features, ...partial?.features },
    layout: { ...defaults.layout, ...partial?.layout },
    themeExtensions: partial?.themeExtensions,
    expiresAt: partial?.expiresAt,
  }
}
